import React, {Component, Fragment} from 'react'
import {Popover, Transition} from '@headlessui/react'
import BellIcon from "@heroicons/react/24/outline/BellIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import {connect} from "react-redux";
import {classNames, generateCustomNotification} from "../../../../util/util-helpers";
import {toFrontDateTimeFromUTC} from "../../../../util/util-dates";
import {clearAllNotifications, clearNotification, fetchMoreNotifications, markAllNotificationsRead, markNotificationRead} from "../../../../package/notification/actions/notifications";
import {LoaderSmall} from "../../../loader";
import {hideGlobalModal} from "../../../../../data/actions/ui";

const LOAD_MORE_SCROLL_THRESHOLD_PX = 100;
const MAX_AUTO_BACKFILL_PAGES = 3;

// Scroll-near-bottom trigger for lazy-loading older history. Pure so it's testable
// without a component renderer (this repo has none installed).
export function shouldLoadMore(scrollTop, clientHeight, scrollHeight, hasMore, isLoadingMore) {
    if (!hasMore || isLoadingMore) {
        return false;
    }
    return scrollTop + clientHeight >= scrollHeight - LOAD_MORE_SCROLL_THRESHOLD_PX;
}

// Big-monitor fallback: if the dropdown's content doesn't overflow its fixed-height
// container, a scroll event may never fire, so this is checked after every render
// too and capped at MAX_AUTO_BACKFILL_PAGES so it can't chain indefinitely.
export function shouldAutoBackfill(scrollHeight, clientHeight, hasMore, backfillCount, maxBackfill = MAX_AUTO_BACKFILL_PAGES) {
    if (!hasMore || backfillCount >= maxBackfill) {
        return false;
    }
    return scrollHeight <= clientHeight;
}

class NotificationsMenu extends Component {

    scrollContainerRef = React.createRef();
    buttonRef = React.createRef();
    backfillCount = 0;

    handleItemClick = (e, item) => {
        this.props.dispatch(markNotificationRead(item.ID));

        // If the click landed on (or inside) a link embedded in the message body,
        // let it navigate natively instead of also firing the row's onClickEvent -
        // a message can embed more than one link (e.g. a document link and a load
        // link in the same notification), each already pointing wherever it should
        // via its own href, and firing onClickEvent too would race a second,
        // possibly different, navigation on top of it. Marking the notification
        // read still happens either way.
        if (e.target.closest('a')) {
            return;
        }

        if (item.onClickEvent) {
            item.onClickEvent();
        }
    }

    handleDismissClick = (e, item) => {
        e.stopPropagation();
        this.props.dispatch(clearNotification(item.ID));
    }

    handleClearAllClick = () => {
        this.props.dispatch(clearAllNotifications());
    }

    handleReadAllClick = () => {
        this.props.dispatch(markAllNotificationsRead());
    }

    // Set as the scrollable container's `ref`: fires on every mount, which for a
    // Headless UI Transition without `unmount={false}` means every time the dropdown
    // opens - the natural point to reset the backfill counter and check whether the
    // page(s) already loaded need an immediate backfill (e.g. reopening after a
    // resize/zoom made the container no longer overflow).
    setScrollContainerRef = (node) => {
        this.scrollContainerRef.current = node;
        if (node) {
            this.backfillCount = 0;
            this.maybeAutoBackfill();
        }
    }

    maybeAutoBackfill = () => {
        const node = this.scrollContainerRef.current;
        if (!node || !!this.props.notifications?.isLoadingMore) {
            return;
        }

        const hasMore = !!this.props.notifications?.data?.nextCursor;
        if (shouldAutoBackfill(node.scrollHeight, node.clientHeight, hasMore, this.backfillCount)) {
            this.backfillCount += 1;
            this.props.dispatch(fetchMoreNotifications());
        }
    }

    handleScroll = () => {
        const node = this.scrollContainerRef.current;
        if (!node) {
            return;
        }

        const hasMore = !!this.props.notifications?.data?.nextCursor;
        const isLoadingMore = !!this.props.notifications?.isLoadingMore;
        if (shouldLoadMore(node.scrollTop, node.clientHeight, node.scrollHeight, hasMore, isLoadingMore)) {
            this.props.dispatch(fetchMoreNotifications());
        }
    }

    componentDidUpdate(prevProps) {
        this.maybeAutoBackfill();

        const wasRequested = !!prevProps.notificationsMenuOpenRequest?.visible;
        const isRequested = !!this.props.notificationsMenuOpenRequest?.visible;
        if (!wasRequested && isRequested) {
            const alreadyOpen = this.buttonRef.current?.getAttribute('aria-expanded') === 'true';
            if (!alreadyOpen) {
                this.buttonRef.current?.click();
            }
            this.props.dispatch(hideGlobalModal('notificationsMenu'));
        }
    }

    render() {
        const {addClass, translate} = this.props;
        const isLoading = !!this.props.notifications?.listIsLoading;
        const isLoadingMore = !!this.props.notifications?.isLoadingMore;
        const unreadCount = this.props.notifications?.data?.unreadCount ?? 0;
        const hasUnreadCountTwoDigits = unreadCount > 9;
        const showBadge = unreadCount > 0;

        const notificationList = this.props.notifications?.data?.notifications?.list?.map(it => {
            it['content'] = it.Message
            it['notificationMetadata'] = it.Metadata
            it['title'] = it.Title

            return Object.assign(
                {...it},
                generateCustomNotification(it, this.props.dispatch, translate, this.props.history)
            )
        })?.reverse() ?? [];

        return (
            <Popover className={classNames(addClass, "relative")}>
                {({open}) => (
                    <>
                        <Popover.Button
                            ref={this.buttonRef}
                            className={classNames(open ? 'text-tm-gray-900' : '', 'icon-btn')}
                        >
                            <BellIcon
                                className={classNames('h-6 w-6 group-hover:text-tm-gray-900')}
                                aria-hidden="true"
                            />
                        </Popover.Button>

                        {!!showBadge && (
                            <span className={classNames("pointer-events-none absolute z-10 flex -top-2.5 -left-2", hasUnreadCountTwoDigits ? "h-6 w-6" : "h-5 w-5")}>
                                <div className="absolute z-10 font-bold inset-0 flex items-center justify-center text-primary-contrast">{unreadCount}</div>
                                <span className="animate-ping absolute z-10 inline-flex h-full w-full rounded-full bg-primary opacity-25"></span>
                                <span className={classNames("relative inline-flex rounded-full bg-primary", hasUnreadCountTwoDigits ? "h-6 w-6" : "h-5 w-5")}/>
                            </span>
                        )}

                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                        >
                            <Popover.Panel
                                className="absolute z-10 w-screen max-w-sm px-4 mt-3 transform -translate-x-1/2 left-1/2 sm:px-0 lg:max-w-sm">
                                <div
                                    className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black/10 bg-popup border border-tm-gray-300">
                                    <div className="relative pt-7 px-7">
                                        <div className="flex items-center justify-between pb-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-tm-gray-900">
                                                    {translate("tab.Notifications")}
                                                </span>

                                                {notificationList.length > 0 && (
                                                    <span className="rounded-full bg-tm-gray-100 px-2 py-0.5 text-xs font-medium text-tm-gray-600">
                                                        {this.props.notifications?.data?.totalCount ?? notificationList.length}
                                                    </span>
                                                )}
                                            </div>

                                            {notificationList.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={this.handleClearAllClick}
                                                    className="text-sm text-primary hover:underline cursor-pointer"
                                                >
                                                    {translate("btn.clear_all")}
                                                </button>
                                            )}
                                        </div>

                                        {unreadCount > 0 && (
                                            <div className="flex items-center justify-between pb-4">
                                                <span className="text-sm text-tm-gray-500">
                                                    {translate("text.unread_count", [unreadCount])}
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={this.handleReadAllClick}
                                                    className="text-sm text-primary hover:underline cursor-pointer"
                                                >
                                                    {translate("btn.mark_all_read")}
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        ref={this.setScrollContainerRef}
                                        onScroll={this.handleScroll}
                                        className="relative grid gap-4 px-7 pb-7 h-dialog-body">
                                        {isLoading && notificationList.length === 0 && (
                                            <div className="relative flex justify-center py-6">
                                                <LoaderSmall/>
                                            </div>
                                        )}

                                        {(!isLoading || notificationList.length > 0) && notificationList.map((item, index) => (
                                            <div
                                                key={item.ID}
                                                onClick={(e) => this.handleItemClick(e, item)}
                                                className={classNames(
                                                    "group relative flex items-start p-2 transition duration-150 ease-in-out cursor-pointer rounded-lg hover:bg-gray-50 focus:outline-hidden focus-visible:ring-3 focus-visible:ring-text-primary500 focus-visible:ring-opacity-50",
                                                    !item.IsRead ? "bg-primary/5" : undefined
                                                )}
                                            >
                                                <span
                                                    className="pointer-events-none absolute -left-5 top-1/2 w-4 -translate-y-1/2 text-right text-sm font-normal text-tm-gray-300"
                                                    aria-hidden="true"
                                                >
                                                    {index + 1}
                                                </span>

                                                {!item.IsRead && (
                                                    <span
                                                        className="mt-2 mr-2 h-2 w-2 shrink-0 rounded-full bg-primary"
                                                        aria-hidden="true"
                                                    />
                                                )}

                                                <div className="ml-2 w-full min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className={classNames(
                                                            "text-sm",
                                                            !item.IsRead ? "font-semibold text-tm-gray-900" : "font-medium text-tm-gray-500"
                                                        )}>
                                                            {item.title}
                                                        </p>

                                                        <button
                                                            type="button"
                                                            onClick={(e) => this.handleDismissClick(e, item)}
                                                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex shrink-0 rounded-md p-0.5 text-tm-gray-400 hover:bg-tm-gray-200 hover:text-tm-gray-500 cursor-pointer"
                                                        >
                                                            <span className="sr-only">{translate("btn.dismiss")}</span>
                                                            <XMarkIcon className="h-4 w-4" aria-hidden="true"/>
                                                        </button>
                                                    </div>

                                                    <div className="text-sm text-tm-gray-500 link-color"
                                                         dangerouslySetInnerHTML={{__html: item.content}}/>

                                                    <p className="mt-1 text-xs text-tm-gray-400">
                                                        {toFrontDateTimeFromUTC(item.DateTime)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}

                                        {!!isLoadingMore && (
                                            <div className="relative flex justify-center py-2">
                                                <LoaderSmall/>
                                            </div>
                                        )}

                                        {!isLoading && (!notificationList || (notificationList.length === 0)) && (
                                            <div className="py-4">
                                                <span className="text-sm text-tm-gray-500">
                                                    {translate("text.no_notifications_list")}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Popover.Panel>
                        </Transition>
                    </>
                )}
            </Popover>
        )
    }
}

export default connect((state) => ({
    user: state.user,
    notifications: state.notifications,
    notificationsMenuOpenRequest: state.ui.globalDialogs?.notificationsMenu
}))(NotificationsMenu);
